import React, { useEffect, useState, useCallback } from "react";
import { api, rupiah, downloadReport } from "@/lib/api";
import { PageHeader, Empty, StatCard, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileBarChart, Wallet, CheckCircle2, AlertTriangle, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const FEE_OPTS = [
  { value: "all", label: "Semua Jenis" },
  { value: "bulanan", label: "Bulanan" },
  { value: "kitab", label: "Kitab" },
  { value: "bsb", label: "BSB" },
  { value: "seragam", label: "Seragam" },
  { value: "kegiatan", label: "Kegiatan" },
];

export default function KeuLaporan() {
  const [data, setData] = useState(null);
  const [angkatans, setAngkatans] = useState([]);
  const [fAng, setFAng] = useState("all");
  const [fFee, setFFee] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fGender, setFGender] = useState("all");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [paidFrom, setPaidFrom] = useState("");
  const [paidTo, setPaidTo] = useState("");

  useEffect(() => { api.get("/admin/angkatans").then((r) => setAngkatans(r.data)).catch(() => {}); }, []);

  const params = useCallback(() => {
    const p = {};
    if (fAng !== "all") p.angkatan = fAng;
    if (fFee !== "all") p.fee_type = fFee;
    if (fStatus !== "all") p.status = fStatus;
    if (fGender !== "all") p.gender = fGender;
    if (dueFrom) p.due_from = dueFrom;
    if (dueTo) p.due_to = dueTo;
    if (paidFrom) p.paid_from = paidFrom;
    if (paidTo) p.paid_to = paidTo;
    return p;
  }, [fAng, fFee, fStatus, fGender, dueFrom, dueTo, paidFrom, paidTo]);

  const load = useCallback(() => {
    api.get("/admin/reports/payments", { params: params() }).then((r) => setData(r.data)).catch(() => {});
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    try {
      await downloadReport("/admin/reports/payments/export", { ...params(), format },
        format === "pdf" ? "laporan_pembayaran.pdf" : "laporan_pembayaran.xlsx");
    } catch (e) {
      toast.error("Gagal mengunduh laporan");
    }
  };

  return (
    <div>
      <PageHeader
        title="Laporan Pembayaran"
        subtitle="Filter berdasarkan angkatan, unit, jenis, status, tanggal jatuh tempo & tanggal bayar. Export ke Excel/PDF."
        action={
          <div className="flex gap-2 no-print">
            <Button onClick={() => doExport("xlsx")} data-testid="export-payments-xlsx" variant="outline" className="rounded-xl">
              <FileSpreadsheet size={18} className="mr-2 text-emerald-600" /> Excel
            </Button>
            <Button onClick={() => doExport("pdf")} data-testid="export-payments-pdf" variant="outline" className="rounded-xl">
              <FileText size={18} className="mr-2 text-rose-600" /> PDF
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <Select value={fAng} onValueChange={setFAng}>
          <SelectTrigger data-testid="report-angkatan" className="w-36 rounded-xl bg-white"><SelectValue placeholder="Angkatan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Angkatan</SelectItem>
            {angkatans.map((a) => <SelectItem key={a} value={String(a)}>Angkatan {a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fGender} onValueChange={setFGender}>
          <SelectTrigger data-testid="report-gender" className="w-32 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Unit</SelectItem>
            <SelectItem value="putra">Putra</SelectItem>
            <SelectItem value="putri">Putri</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fFee} onValueChange={setFFee}>
          <SelectTrigger data-testid="report-feetype" className="w-36 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FEE_OPTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="report-status" className="w-36 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="paid">Sudah Dibayar</SelectItem>
            <SelectItem value="unpaid">Belum Dibayar</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <Label className="text-xs text-stone-400">Jatuh Tempo Dari</Label>
          <Input type="date" data-testid="report-due-from" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} className="rounded-xl bg-white" />
        </div>
        <div>
          <Label className="text-xs text-stone-400">Jatuh Tempo Sampai</Label>
          <Input type="date" data-testid="report-due-to" value={dueTo} onChange={(e) => setDueTo(e.target.value)} className="rounded-xl bg-white" />
        </div>
        <div>
          <Label className="text-xs text-stone-400">Tgl Bayar Dari</Label>
          <Input type="date" data-testid="report-paid-from" value={paidFrom} onChange={(e) => setPaidFrom(e.target.value)} className="rounded-xl bg-white" />
        </div>
        <div>
          <Label className="text-xs text-stone-400">Tgl Bayar Sampai</Label>
          <Input type="date" data-testid="report-paid-to" value={paidTo} onChange={(e) => setPaidTo(e.target.value)} className="rounded-xl bg-white" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <StatCard icon={FileBarChart} label="Jumlah Tagihan" value={data?.count ?? "–"} />
        <StatCard icon={Wallet} label="Total Tagihan" value={data ? rupiah(data.total) : "–"} accent="blue" />
        <StatCard icon={CheckCircle2} label="Terbayar" value={data ? rupiah(data.paid) : "–"} accent="emerald" />
        <StatCard icon={AlertTriangle} label="Tunggakan" value={data ? rupiah(data.outstanding) : "–"} accent="rose" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {!data || data.rows.length === 0 ? (
          <Empty><FileBarChart className="mx-auto mb-2" /> Tidak ada data sesuai filter.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Santri</TableHead>
                <TableHead>Angkatan</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Tagihan</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Dibayar</TableHead>
                <TableHead>Jatuh Tempo</TableHead>
                <TableHead>Tgl Bayar</TableHead>
                <TableHead>Petugas</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                  <TableCell className="font-medium text-stone-800">{r.student_name}</TableCell>
                  <TableCell>{r.angkatan}</TableCell>
                  <TableCell className="capitalize">{r.gender}</TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(r.total_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(r.paid_amount)}</TableCell>
                  <TableCell className="text-sm">{r.due_date}</TableCell>
                  <TableCell className="text-sm">{r.paid_at || "-"}</TableCell>
                  <TableCell className="text-sm text-stone-500">{r.source || "-"}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
